import { getToken } from '../utils/auth/tokenManager';

const API_BASE = process.env.REACT_APP_API_URL;

class ChatEventService {
  constructor() {
    this.eventSource = null;
    this.listeners = new Set();
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    /** Base delay (ms); actual delay uses exponential backoff to avoid 429 reconnect storms */
    this.reconnectDelayBase = 3000;
    /** Pending reconnect timer id so it can be cancelled on disconnect */
    this.reconnectTimerId = null;
    /** Set when disconnect() is called deliberately so we don't auto-reconnect */
    this.intentionalDisconnect = false;
  }

  /**
   * Connect to the SSE endpoint
   */
  async connect() {
    if (this.eventSource && this.eventSource.readyState !== EventSource.CLOSED) {
      // Already connected or connecting
      return;
    }

    const token = getToken();
    if (!token) {
      // eslint-disable-next-line no-console
      console.warn('[ChatEventService] No token found, cannot connect to SSE');
      return;
    }

    try {
      // Close existing connection if any
      this.disconnect();
      // This is a deliberate (re)connect attempt, so re-enable auto-reconnect.
      this.intentionalDisconnect = false;

      // Exchange the long-lived JWT for a short-lived, single-use SSE session
      // token. This keeps the JWT out of URLs, Referer headers, and access logs.
      const sessionResponse = await fetch(`${API_BASE}/api/chat_events/session`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!sessionResponse.ok) {
        // eslint-disable-next-line no-console
        console.error('[ChatEventService] Failed to obtain SSE session token:', sessionResponse.status);
        this.scheduleReconnect();
        return;
      }

      const { sseToken } = await sessionResponse.json();
      if (!sseToken) {
        // eslint-disable-next-line no-console
        console.error('[ChatEventService] No SSE session token returned');
        this.scheduleReconnect();
        return;
      }

      const url = `${API_BASE}/api/chat_events?sse=${encodeURIComponent(sseToken)}`;
      this.eventSource = new EventSource(url);

      this.eventSource.onopen = () => {
        this.isConnected = true;
        this.reconnectAttempts = 0;
        // eslint-disable-next-line no-console
        console.log('[ChatEventService] Connected to SSE');
        this.notifyListeners({ type: 'connected' });
      };

      this.eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'heartbeat') {
            // Ignore heartbeat messages
            return;
          }

          if (data.type === 'connected') {
            this.isConnected = true;
            this.notifyListeners({ type: 'connected' });
            return;
          }

          if (data.type === 'newMessage') {
            this.notifyListeners({
              type: 'newMessage',
              conversationType: data.conversationType,
              conversationId: data.conversationId,
              message: data.message,
            });
          }
        } catch (error) {
          console.error('[ChatEventService] Error parsing SSE message:', error);
        }
      };

      this.eventSource.onerror = (error) => {
        // eslint-disable-next-line no-console
        console.error('[ChatEventService] SSE error:', error);
        this.isConnected = false;
        this.notifyListeners({ type: 'error', error });
        this.scheduleReconnect();
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[ChatEventService] Error creating EventSource:', error);
      this.isConnected = false;
      this.scheduleReconnect();
    }
  }

  /**
   * Schedule a reconnection attempt with exponential backoff and jitter.
   */
  scheduleReconnect() {
    // Do not reconnect after an intentional disconnect (logout / unmount).
    if (this.intentionalDisconnect) {
      return;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      // eslint-disable-next-line no-console
      console.error('[ChatEventService] Max reconnection attempts reached');
      this.notifyListeners({ type: 'maxReconnectAttemptsReached' });
      return;
    }

    // Cancel any previously scheduled reconnect so timers can't stack up.
    if (this.reconnectTimerId) {
      clearTimeout(this.reconnectTimerId);
      this.reconnectTimerId = null;
    }

    this.reconnectAttempts += 1;
    const exp = Math.min(
      this.reconnectDelayBase * (2 ** (this.reconnectAttempts - 1)),
      120000,
    );
    const jitter = Math.floor(Math.random() * 800);
    const delayMs = exp + jitter;
    this.reconnectTimerId = setTimeout(() => {
      this.reconnectTimerId = null;
      // eslint-disable-next-line no-console
      console.log(`[ChatEventService] Reconnecting (attempt ${this.reconnectAttempts}, in ${delayMs}ms)...`);
      this.connect();
    }, delayMs);
  }

  /**
   * Disconnect from the SSE endpoint
   */
  disconnect() {
    // Mark as intentional and cancel any pending reconnect so a scheduled
    // reconnect can't fire after logout/unmount.
    this.intentionalDisconnect = true;
    if (this.reconnectTimerId) {
      clearTimeout(this.reconnectTimerId);
      this.reconnectTimerId = null;
    }

    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
      this.isConnected = false;
      // eslint-disable-next-line no-console
      console.log('[ChatEventService] Disconnected from SSE');
    }
  }

  /**
   * Add a listener for SSE events
   * @param {Function} listener - Callback function that receives event data
   * @returns {Function} Unsubscribe function
   */
  addListener(listener) {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all listeners of an event
   * @param {Object} eventData - The event data to send to listeners
   */
  notifyListeners(eventData) {
    this.listeners.forEach((listener) => {
      try {
        listener(eventData);
      } catch (error) {
        console.error('[ChatEventService] Error in listener:', error);
      }
    });
  }

  /**
   * Check if currently connected
   */
  getConnectionStatus() {
    return this.isConnected;
  }
}

const chatEventService = new ChatEventService();

export default chatEventService;
